import { Popover, Tooltip } from "@mui/material";
import { ArrowLeft, Eye, Filter, PlusCircle } from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import API from "../../API/newIndex";
import { DataLoad, PlanPermission, TableColumnsContext } from "../../App";
import { NOPHOTO_IMAGE } from "../../Assets/StaticData/StaticImage";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { getInitialPage, replacePageInUrl } from "../../controllers/PageHandleFromQuery";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { useUser } from "../../controllers/UserContext";
import RenderCustomFieldValue from "../../utilities/RenderCustomFieldValue";
import { ButtonComponent, CommonTable, PageLimitDropDownComponent, PaginationComponent, SearchComponent } from '@shopeasy-dashboard-ui/ui-library';
import { isAscendingOrder } from "../FormInput/CommonTable";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import AddPortfolioComments from "./AddPortfolioComments";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import useTableApi from "../../utilities/useTableApi";

const PortfolioComments = () => {
  const handleTableApi = useTableApi();
  const { tableColumnsData } = useContext(TableColumnsContext);
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.portfoliocomments || sortData?.comments;

  const initialLimit = localStorage.getItem("limit");
  const [limit, setLimit] = useState(initialLimit || 10);
  const [Page, setPage] = useState(getInitialPage());
  const [comments, setComments] = useState();
  const { setPlanPermission } = useContext(PlanPermission);
  const [sortBy, setSortBy] = useState(() => savedSort?.sort_by || "");
  const [orderBy, setOrderBy] = useState(() => {
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(savedSort?.sort_by));

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const commentRef = useRef(null);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.portfoliocomments)) return dataObj.portfoliocomments;
    if (Array.isArray(dataObj?.portfolio_comments)) return dataObj.portfolio_comments;
    if (Array.isArray(dataObj?.comments)) return dataObj.comments;
    if (Array.isArray(tableColumnsData?.portfoliocomments)) return tableColumnsData.portfoliocomments;
    if (Array.isArray(tableColumnsData?.comments)) return tableColumnsData.comments;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'image', 'title', 'comment_count', 'action', 'actions',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' • ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        const isImageCol =
          fieldName.toLowerCase().includes('image') ||
          fieldName.toLowerCase().includes('photo') ||
          fieldName.toLowerCase().includes('img') ||
          fieldName.toLowerCase().includes('thumbnail') ||
          fieldName.toLowerCase().includes('logo') ||
          fieldName.toLowerCase().includes('banner') ||
          fieldName.toLowerCase().includes('icon');

        return {
          key: fieldName,
          title: label,
          width: isImageCol ? '80px' : '160px',
          minWidth: isImageCol ? '70px' : '110px',
          maxWidth: isImageCol ? '95px' : undefined,
          responsiveWidth: isImageCol ? '70px' : '100px',
          align: isImageCol ? 'text-center' : undefined,
          headerClassName: isImageCol
            ? 'thead-image px-2 text-center whitespace-nowrap'
            : 'px-3 whitespace-nowrap truncate',
          cellClassName: isImageCol
            ? 'table-image px-2 text-center'
            : 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            return <RenderCustomFieldValue val={val} fieldName={fieldName} />;
          },
        };
      });
  }, [apiFieldNames]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    replacePageInUrl(Page);
  }, [Page]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  const [search, setSearch] = useState("");
  var startRecord = (comments?.data?.currentPage - 1) * limit + 1;
  var endRecord = Math.min(comments?.data?.currentPage * limit, comments?.data?.totalRecords);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    const trimmed = search?.trim() || "";
    clearTimeout(debounceTimeout.current);
    if (trimmed === "") {
      GetPortfolioComments();
    } else if (trimmed.length >= 3) {
      debounceTimeout.current = setTimeout(() => {
        GetPortfolioComments();
      }, 500);
    }
    return () => clearTimeout(debounceTimeout.current);
  }, [Page, limit, sortBy, orderBy, search]);

  const GetPortfolioComments = async () => {
    setDataLoading(true);
    try {
      const data = await API({
        url: `/admin/portfolio/GetPortfolioComments?searchkey=${search?.length > 2 ? search : ""
          }&page=${Page}&limit=${limit}&sort_by=${sortBy}&order_by=${orderBy.orderByValue}`,
        method: "get",
      });
      if (data.status === 200 || data.status === 304) {
        const result = data?.data;
        setComments(result);
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "portfolio" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const resetFilter = (e) => {
    e.preventDefault();
    setSortBy("");
    setSearch("");
    setOrderBy({ orderByValue: "", isDecending: false });
    setFilter(false);
    setAnchorEl(null);
    setLimit(limit === "all" ? 10 : limit);
  };

  const [isAddComment, setIsAddComment] = useState(false);
  const closeAddComment = () => {
    setIsAddComment(false);
    GetPortfolioComments();
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const columns = [
    {
      key: "image",
      width: "75px",
      minWidth: "70px",
      maxWidth: "85px",
      responsiveWidth: "60px",
      title: t("lang_Image") || "Image",
      headerClassName: "thead-image px-2 text-center",
      cellClassName: "table-image px-2 text-center",
      render: (row) => (
        <img
          width={40}
          height={50}
          onError={(e) => {
            e.target.src = NOPHOTO_IMAGE;
          }}
          src={row?.image || row?.portfolio_id?.image || NOPHOTO_IMAGE}
          className="p-0.5 max-h-full rounded-md mx-auto block"
          alt={row?.title || row?.portfolio_id?.title}
        />
      ),
    },
    {
      key: "title",
      title: t("lang_Portfolio_Title") || "Portfolio Title",
      width: "500px",
      maxWidth: "550px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <p className="line-clamp-2 text-xs text-[var(--primary-text-color)] font-medium">
          {row?.title || row?.portfolio_id?.title || "-"}
        </p>
      ),
    },
    {
      key: "comment_count",
      title: t("lang_Comment_Count") || "Comment Count",
      width: "140px",
      maxWidth: "150px",
      headerClassName: "px-3 whitespace-nowrap",
      cellClassName: "px-3 whitespace-nowrap",
      render: (row) => (
        <span className="text-xs font-semibold text-[var(--primary-text-color)]">
          {row?.comment_count ?? 0}
        </span>
      ),
    },
    ...dynamicColumns,
    {
      key: "action",
      title: t("lang_action"),
      width: "80px",
      minWidth: "80px",
      responsiveWidth: "80px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <div className="flex items-center justify-end w-full space-x-3">
          <Tooltip title="View Comment" arrow>
            <button onClick={() => parentNavigate(`/portfolio/comments/${row?._id}`)}>
              <Eye className="w-[16px] h-[16px] text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => {
    const totalCount = comments?.data?.totalRecords || comments?.data?.records?.length || 0;
    return (dataLoading || totalCount > 0 || search) ? (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="hidden md:flex items-center gap-2 ml-auto">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder="Search Portfolio Comments"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>

            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={totalCount}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <Tooltip title={t("lang_filter") || "Filter"} arrow>
            <button
              type="button"
              onClick={(e) => {
                setFilter(true);
                setAnchorEl(e.currentTarget);
              }}
              className="block md:hidden relative flex items-center justify-center p-2 border main-border-color rounded-lg bg-[var(--secondary-bg-color)] hover:bg-gray-50/50 transition-colors w-8 h-8 shrink-0"
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </button>
          </Tooltip>
        </div>
      </div>
    ) : null;
  };

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow -ml-3.5"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_Portfolio_Comments"), path: "/portfolio/comments" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {t("lang_Portfolio_Comments")}
              </h4>
            </div>
          </div>
          <div className="flex items-center">
            <ButtonComponent
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              onClick={(e) => {
                e.preventDefault();
                setIsAddComment(true);
              }}
            >
              {t("lang_Add_Portfolio_Comments")}
            </ButtonComponent>
          </div>
        </div>
        <div className="flex justify-between w-full space-x-4">
          <div className="w-full space-y-3">
            <div className="flex w-full h-full overflow-hidden relative min-w-0">
              <div className="w-full">
                <div className="w-full transition-all duration-100 mb-[10px]">
                  <CommonTable
                    tableId="portfoliocomments"
                    columns={columns}
                    data={comments?.data?.records}
                    tableHeader={tableHeaderFilter}
                    onSort={SortData}
                    sortBy={sortBy}
                    orderBy={orderBy}
                    tableFooter={
                      (comments?.data?.totalRecords > 0 || comments?.data?.records?.length > 0) ? (
                        <PaginationComponent
                          count={comments?.data?.totalPages || 1}
                          page={Page}
                          onChange={(e, v) => setPage(v)}
                          totalRecords={comments?.data?.totalRecords || comments?.data?.records?.length || 0}
                          startRecord={startRecord}
                          endRecord={endRecord}
                          limit={limit}
                          recordsLabel="Records"
                        />
                      ) : null
                    }
                    loading={dataLoading}
                    emptyMessage={<NoRecords />}
                    rowClick={(row) => parentNavigate(`/portfolio/comments/${row?._id}`)}
                    NEWAPI={handleTableApi}
                    tableDefaults={defaultCustomFields?.defaultPortfolioCommentsField}
                    tableWiseAllowedSortKeys={defaultCustomFields?.PORTFOLIO_COMMENTS_SORT_FIELD}
                    userState={userContext}
                  />
                </div>
              </div>
              <Popover
                open={filter}
                anchorEl={anchorEl}
                onClose={() => {
                  setFilter(false);
                  setAnchorEl(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
                }}
                className="rounded-[6px]"
              >
                <div className="p-[20px] filter secondary-bg-color">
                  <div className="flex justify-between items-center">
                    <p className="flex font-semibold uppercase text-sm m-0">
                      <Filter className="me-1 w-4 h-4" />
                      {t("lang_filter")}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => resetFilter(e)}
                      className="text-white bg-gray-800 hover:bg-gray-900 focus:outline-none font-medium rounded-md text-sm px-5 py-2 me-1 mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                    >
                      Reset Filter
                    </button>
                  </div>
                  <hr className="main-border-color my-3"></hr>
                  <div className="grid gap-y-5 table-text">
                    <div>
                      <span className="text-sm capitalize font-medium mb-1 inline-block">
                        {t("lang_Search")}
                      </span>
                      <SearchComponent
                        placeholder="Search Portfolio Comments"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        onClear={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        iconPlacement="left"
                        isClearable={true}
                        size="sm"
                      />
                    </div>
                    <div>
                      <span className="text-sm font-medium mb-1 inline-block">
                        {t("lang_set_items_limit")}
                      </span>
                      <PageLimitDropDownComponent
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        totalRecords={comments?.data?.totalRecords || 0}
                      />
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
          </div>
        </div>
      </div>
      <AddPortfolioComments
        commentRef={commentRef}
        isAddComment={isAddComment}
        closeAddComment={closeAddComment}
        type="add"
      />
    </>
  );
};

export default PortfolioComments;
